/**
 * AbortController Mixin
 *
 * Provides functionality to abort pending API requests when new requests are made.
 * This prevents race conditions when multiple requests are triggered rapidly.
 *
 * Usage:
 * 1. Import and add to component mixins
 * 2. Call createAbortController() before making API call to get signal
 * 3. Pass signal to axios config: { signal }
 * 4. Handle AbortError in catch block
 *
 * Example:
 * async tableRows() {
 *   const signal = this.createAbortController();
 *   try {
 *     const data = await api.fetch(params, { signal });
 *     // handle data
 *   } catch (error) {
 *     if (axios.isCancel(error)) {
 *       return; // Ignore aborted requests
 *     }
 *     // handle other errors
 *   }
 * }
 */
export default {
  data() {
    return {
      currentAbortController: null,
    };
  },
  methods: {
    /**
     * Creates a new AbortController and cancels any pending request
     * @returns {AbortSignal} Signal to pass to axios config
     */
    createAbortController() {
      // Cancel previous request if exists
      if (this.currentAbortController) {
        this.currentAbortController.abort();
      }

      // Create new AbortController
      this.currentAbortController = new AbortController();
      return this.currentAbortController.signal;
    },

    /**
     * Manually cancel all pending requests
     */
    cancelPendingRequests() {
      if (this.currentAbortController) {
        this.currentAbortController.abort();
        this.currentAbortController = null;
      }
    },
  },
  beforeDestroy() {
    // Clean up: cancel any pending requests when component is destroyed
    this.cancelPendingRequests();
  },
};
